/**
 * appleAuth.js — TreatX Sign in with Apple (web) helper.
 * =====================================================
 *
 * TREATX-APPLE-OAUTH-FRONTEND-01 (Sprint parallel workstream).
 *
 * Loads the official Apple Sign in with Apple JS SDK on demand,
 * initialises it with the TreatX Services ID + return URI, and
 * exposes a single `signInWithApple()` that returns the identity
 * token payload for POSTing to `/api/auth/social/apple`.
 *
 * Design invariants:
 *  - SDK is loaded from Apple's official CDN
 *    (`https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js`)
 *    only the first time `signInWithApple()` is called. If the SDK is
 *    already present (e.g. injected in `public/index.html`), the loader
 *    short-circuits.
 *  - `AppleID.auth.init()` is called with `usePopup: true` and
 *    `scope: 'name email'` so the identity token returns in the popup
 *    response — no server-side authorization-code exchange is needed
 *    for the current TreatX flow. The `.p8` client secret is therefore
 *    NOT required for basic login (only for future refresh-token /
 *    revocation flows).
 *  - The client_id is read from `process.env.REACT_APP_APPLE_CLIENT_ID`
 *    at call time. If it is missing we throw the sentinel
 *    `AppleAuthNotConfiguredError` so callers can render a clean
 *    "coming soon" state without any Apple network calls.
 *  - The redirect URI is `${window.location.origin}/auth/apple/callback`;
 *    with usePopup:true the browser never actually navigates there —
 *    Apple only validates that the string is byte-exact against a
 *    Return URL registered on the Services ID.  See appleAuth.js:92
 *    for the single source of truth.
 *  - No client-side route needs to exist at /auth/apple/callback —
 *    with `usePopup:true`, Apple returns the identity_token to the
 *    parent window via postMessage; the browser never navigates to
 *    the redirect URI. Apple only validates that the URI is byte-
 *    exact against a Return URL registered on the Services ID, so
 *    TreatX must register every deployment host + `/auth/apple/callback`.
 *  - The identity_token and authorization code are never logged.
 */

const APPLE_SDK_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

export class AppleAuthNotConfiguredError extends Error {
  constructor() {
    super('REACT_APP_APPLE_CLIENT_ID is not set — Apple Sign-In is not configured.');
    this.name = 'AppleAuthNotConfiguredError';
    this.code = 'apple_not_configured';
  }
}

export class AppleAuthPopupError extends Error {
  constructor(cause) {
    super(cause?.error || cause?.message || 'Apple Sign-In popup was closed or blocked.');
    this.name = 'AppleAuthPopupError';
    this.code = cause?.error || 'popup_closed';
  }
}

let _sdkLoadPromise = null;
let _initialised = false;

function _loadSdk() {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'));
  if (window.AppleID?.auth) return Promise.resolve();
  if (_sdkLoadPromise) return _sdkLoadPromise;
  _sdkLoadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${APPLE_SDK_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('apple_sdk_load_failed')));
      return;
    }
    const el = document.createElement('script');
    el.src = APPLE_SDK_SRC;
    el.async = true;
    el.defer = true;
    el.crossOrigin = 'anonymous';
    el.onload = () => resolve();
    el.onerror = () => reject(new Error('apple_sdk_load_failed'));
    document.head.appendChild(el);
  });
  return _sdkLoadPromise;
}

function _initIfNeeded() {
  const clientId = (process.env.REACT_APP_APPLE_CLIENT_ID || '').trim();
  if (!clientId) throw new AppleAuthNotConfiguredError();
  if (_initialised) return;
  // TREATX-APPLE-REAL-UAT-MISSING-ID-TOKEN-01
  // Apple's Web SSO forces `response_mode=form_post` whenever any scope
  // (name/email) is requested — this is documented Apple behaviour.
  // The registered Return URL MUST therefore be a real backend endpoint
  // that can accept an application/x-www-form-urlencoded POST. The
  // TreatX FastAPI route `/api/auth/apple/callback` handles that POST,
  // verifies the credential, stashes a one-time handle in Mongo, and
  // 302-redirects the browser to `${origin}/#/auth/apple/callback` so
  // the SPA (HashRouter) can complete the login.
  //
  // Apple Portal Return URLs to register on the Services ID:
  //   https://treatx.health/api/auth/apple/callback
  //   https://candidate-union.preview.emergentagent.com/api/auth/apple/callback
  const redirectURI = `${window.location.origin}/api/auth/apple/callback`;
  // Generate CSRF-safe state; Apple echoes this back verbatim in the
  // form_post body and (via the backend) in the SPA query string. The
  // SPA compares it with sessionStorage on receipt.
  const state = _issueState();
  window.AppleID.auth.init({
    clientId,
    scope: 'name email',
    redirectURI,
    usePopup: false,
    responseMode: 'form_post',
    responseType: 'code id_token',
    state,
  });
  _initialised = true;
}

const APPLE_STATE_KEY = '_treatx_apple_state';
function _issueState() {
  const bytes = new Uint8Array(24);
  (window.crypto || window.msCrypto).getRandomValues(bytes);
  const state = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
  try { sessionStorage.setItem(APPLE_STATE_KEY, state); } catch { /* ignore */ }
  return state;
}

export function readAndClearAppleState() {
  try {
    const v = sessionStorage.getItem(APPLE_STATE_KEY);
    sessionStorage.removeItem(APPLE_STATE_KEY);
    return v;
  } catch { return null; }
}

export function isAppleAuthConfigured() {
  return Boolean((process.env.REACT_APP_APPLE_CLIENT_ID || '').trim());
}

/**
 * Trigger Apple Sign-In using redirect flow (iOS-Safari safe).
 *
 * Never resolves — the browser navigates away to Apple. Apple then
 * redirects back to `${origin}/auth/apple/callback#…id_token=…` which
 * is picked up by pages/AppleCallback.jsx.
 *
 * Throws AppleAuthNotConfiguredError if the Services ID is missing.
 */
export async function signInWithApple() {
  if (!isAppleAuthConfigured()) throw new AppleAuthNotConfiguredError();
  await _loadSdk();
  _initIfNeeded();
  try {
    // In redirect mode Apple's SDK performs `window.location.assign(...)`.
    // The call returns without resolving; we simply await it defensively.
    await window.AppleID.auth.signIn();
  } catch (err) {
    throw new AppleAuthPopupError(err);
  }
}
